import { useEffect, useMemo, useRef, useState } from 'react'
import { matchPlaces, type Place } from '../../shared/places'
import { useI18n } from '../lib/i18n'

interface Props {
  onPick: (place: Place) => void
  autoFocus?: boolean
}

/**
 * Type-ahead over Madeira's towns, used when the browser will not hand over a
 * location. Suggestions appear before a single keystroke, so the fast path is
 * "click, click" rather than typing a full place name.
 */
export function PlacePicker({ onPick, autoFocus }: Props) {
  const { t } = useI18n()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const box = useRef<HTMLDivElement>(null)

  const matches = useMemo(() => matchPlaces(query), [query])

  useEffect(() => setActive(0), [query])

  // A click anywhere else is a dismissal, not a selection.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const choose = (place: Place) => {
    setQuery('')
    setOpen(false)
    onPick(place)
  }

  return (
    <div ref={box} className="relative">
      <input
        type="text"
        value={query}
        autoFocus={autoFocus}
        placeholder={t.placePlaceholder}
        aria-label={t.placeLabel}
        role="combobox"
        aria-expanded={open}
        aria-controls="place-options"
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setOpen(true)
            setActive((i) => Math.min(i + 1, matches.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((i) => Math.max(i - 1, 0))
          } else if (e.key === 'Enter' && open && matches[active]) {
            e.preventDefault()
            choose(matches[active])
          } else if (e.key === 'Escape') {
            setOpen(false)
          }
        }}
        className="w-full rounded-full border px-3 py-1 text-xs outline-none sm:w-56"
        style={{
          borderColor: 'var(--border-strong)',
          background: 'var(--surface)',
          color: 'var(--text)',
        }}
      />

      {open && matches.length > 0 && (
        <ul
          id="place-options"
          role="listbox"
          className="card absolute z-40 mt-1 max-h-56 w-full overflow-y-auto py-1 sm:w-56"
        >
          {matches.map((place, i) => (
            <li key={place.name} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(place)}
                className="w-full px-3 py-1.5 text-left text-xs transition-colors"
                style={{
                  background: i === active ? 'var(--surface-2)' : 'transparent',
                  color: 'var(--text)',
                }}
              >
                {place.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
